import { ConfigProvider } from "antd";
import "antd/dist/reset.css";
import React, { useEffect } from "react";
import { Provider, useSelector } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { ProjectName } from "./config";
import { persistor, store } from "./redux/store";
import Routers from "./routers";
import GlobalStyles from "./styles/GlobalStyles";

/**
 * PENDO INITIALIZATION WRAPPER
 * This component listens to the Redux store. When a user logs in,
 * it passes their identity to Pendo.
 */
function PendoWrapper({ children }) {
  // Grab user data from Redux state
  const userData = useSelector((state) => state.user?.user);

  useEffect(() => {
    // Only initialize Pendo if:
    // 1. We have user data (logged in)
    // 2. The Pendo script in index.html has loaded window.pendo
    if (userData && window.pendo && typeof window.pendo.initialize === "function") {
      const visitorId = userData.user_data?.id || userData.id;

      if (visitorId) {
        window.pendo.initialize({
          visitor: {
            id: visitorId, // REQUIRED: Must be 'id'
            email: userData.user_data?.email,
            role: userData.user_data?.user_type,
            full_name: userData.user_data?.full_name,
          },
          account: {
            // Optional: If you have company data, map it here
            id: userData.user_data?.company_id || "INDIVIDUAL",
            name: userData.user_data?.company_name || "Personal Account",
          },
        });
        console.log("Pendo initialized for visitor:", visitorId);
      }
    }
  }, [userData]); // Re-run whenever the user logs in or out

  return children;
}

function App() {
  document.title = ProjectName;

  const handleLoad = () => {
    console.log("Page fully loaded including stylesheets, images, etc.");
  };

  useEffect(() => {
    window.addEventListener("load", handleLoad);
    return () => {
      window.removeEventListener("load", handleLoad);
    };
  }, []);

  return (
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          {/* We wrap the app logic with PendoWrapper so it has access to Redux state */}
          <PendoWrapper>
            <ConfigProvider
                theme={{
                  token: {
                    fontFamily: `"Inter", sans-serif`,
                  },
                  components: {
                    Checkbox: {
                      colorPrimary: "#F45B68",
                      colorPrimaryHover: "#cd404c",
                      colorPrimaryActive: "#F45B68",
                      borderRadius: 4,
                      controlHeight: 20,
                      size: 16,
                      borderColor: "#ddd",
                      colorBgContainer: "#fff",
                      colorText: "#333",
                      colorTextDisabled: "#bdbdbd",
                      colorBorder: "#ddd",
                    },
                    Switch: {
                      colorPrimary: "#F45B68",
                      colorPrimaryHover: "#cd404c",
                      colorPrimaryActive: "#F45B68",
                      handleBg: "#fff",
                      trackHeight: 26,
                      trackMinWidth: 38,
                      handleSize: 18,
                      borderRadius: 22,
                      colorText: "#333",
                      colorTextTertiary: "#bdbdbd",
                      colorBorder: "#ddd",
                    },
                      Input: {
                          colorPrimary: '#F45B68',
                          colorPrimaryHover: '#cd404c',
                          colorPrimaryActive: '#F45B68',
                          borderRadius: 8,
                          colorBgContainer: "#fff",
                          colorText: "#333",
                          // colorTextPlaceholder: "#bdbdbd",
                          colorBorder: "#ddd",
                          controlHeight: 40,
                          fontSize: 13,
                      },
                  },
                }}
            >
              <GlobalStyles />
              <Routers />
            </ConfigProvider>
          </PendoWrapper>
        </PersistGate>
      </Provider>
  );
}

export default App;